import { intl } from "LocaleWrapper";
import { Session } from "ezStore";
import { hasPluginPermission, isSkipper, usePermission } from "imports/Utils";
import { StatusFormInput, StatusFormOutput } from "./schema";
import { StatusActualTimeType, StatusEstimateRequirement, StatusEstimateType, StatusType } from "__generated__/enums";
import { StatusChangeFragmentFragment as StatusChangeFragment, StatusFragmentFragment, StatusFragmentFragment as StatusFragment } from "__generated__/view/statuses/graphql/fragments.generated";
import { ChildPropsFromArray } from "types/commons";
import { GetStatusRuleTypesQuery } from "__generated__/view/statuses/graphql/queries.generated";
import { isShipManager } from "components/utils/Permission";

export enum StatusHistoryView {
	Accordion = "accordion",
	Table = "Table"
}

export const getStatusTypeByNumber = (type: number) => {
	switch (type) {
		case 1:
			return StatusType.Cargo;
		case 2:
			return StatusType.Call;
		case 3:
			return StatusType.Voyage;
		case 6:
			return StatusType.Declaration;
		default:
			return undefined;
	}
};

export const buildStatusOptions = (session: Session, statusType: StatusType, lastStatusChange?: StatusChangeFragment | null) => {
	const { isAdmin, isCompanyUser, isMainUserLimited } = usePermission();

	const successiveStatuses = lastStatusChange?.status?.successiveStatuses || [];

	const isAllowedToSkipStatuses =
		hasPluginPermission(session, "mainUserMaySkipCargoStatusFlow") &&
		(isAdmin || isCompanyUser || isMainUserLimited) &&
		statusType === StatusType.Cargo;

	if (isAllowedToSkipStatuses) {
		// TODO: sometimes the session's statuses are empty/null, we should fix this...
		return filterAllStatuses(session.statuses?.[statusType] ?? [], lastStatusChange);
	} else if (hasPluginPermission(session, "successiveStatuses")) {
		return filterStatuses(successiveStatuses);
	} else {
		return filterStatuses(session.statuses?.[statusType] ?? []);
	}
};

const filterAllStatuses = (statuses: StatusFragment[], lastStatusChange?: StatusChangeFragment | null) => {
	return statuses.filter((s) => {
		if (s.statusOrder == null || lastStatusChange?.status.statusOrder == null) {
			return false;
		}
		return s.statusOrder > lastStatusChange.status.statusOrder;
	});
};

export const filterStatuses = (statuses: StatusFragment[]) => {
	const { isAdmin, isCompanyUser, isCustomer, isTerminal, isSkipperOrShipManager, isOperator, isMainUserLimited, isReadOnly } = usePermission();

	return statuses.filter(s => {
		if (isReadOnly) {
			return false;
		}
		if (isAdmin || isCompanyUser || isMainUserLimited) {
			return true;
		}
		if (isCustomer) {
			return s.applyByCustomer;
		}
		if (isTerminal) {
			return s.applyByTerminal;
		}
		if (isSkipperOrShipManager || isOperator) {
			return s.applyBySkipper;
		}
		return false;
	});
};

export const shouldShowEstimateTime = (estimateType: StatusEstimateType | null | undefined) => {
	return !!estimateType;
};

export const isEstimateTimeRequired = (estimateRequirement: StatusEstimateRequirement) => {
	return estimateRequirement == StatusEstimateRequirement.Required;
};

export const getEstimatedStatusLabel = (estimateType: StatusEstimateType | undefined | null) => {
	switch (estimateType) {
		case StatusEstimateType.Eta:
			return intl.formatMessage({ id: "STATUS_ETA_FULL" });
		case StatusEstimateType.Etd:
			return intl.formatMessage({ id: "STATUS_ETD_FULL" });
		case StatusEstimateType.Etso:
			return intl.formatMessage({ id: "STATUS_ETSO_FULL" });
		case StatusEstimateType.Eteo:
			return intl.formatMessage({ id: "STATUS_ETEO_FULL" });
		case StatusEstimateType.StatusChange:
			return intl.formatMessage({ id: "ESTIMATED_TIME" });
		default:
			return "";
	}
};

export const getEstimatedStatusAbbreviation = (estimateType?: StatusEstimateType | null) => {
	switch (estimateType) {
		case StatusEstimateType.Eta:
			return intl.formatMessage({ id: "STATUS_ETA" });
		case StatusEstimateType.Etd:
			return intl.formatMessage({ id: "STATUS_ETD" });
		case StatusEstimateType.Etso:
			return intl.formatMessage({ id: "STATUS_ETSO" });
		case StatusEstimateType.Eteo:
			return intl.formatMessage({ id: "STATUS_ETEO" });
		case StatusEstimateType.StatusChange:
			return intl.formatMessage({ id: "ESTIMATED_TIME" });
		default:
			return "";
	}
};

export const shouldShowActualTime = (requireActualTime: number | StatusActualTimeType) => {
	return typeof requireActualTime === "number" ? (requireActualTime > 0) : (requireActualTime !== StatusActualTimeType.NotRequired);
};

export const getActualStatusLabel = (requireActualTime: StatusActualTimeType) => {
	if (requireActualTime === StatusActualTimeType.NotRequired) {
		return intl.formatMessage({ id: "STATUS_TIMESTAMP_TYPE_0" });
	} else if (requireActualTime === StatusActualTimeType.Ata) {
		return intl.formatMessage({ id: "STATUS_TIMESTAMP_TYPE_1" });
	} else if (requireActualTime === StatusActualTimeType.Atd) {
		return intl.formatMessage({ id: "STATUS_TIMESTAMP_TYPE_2" });
	} else if (requireActualTime === StatusActualTimeType.Atso) {
		return intl.formatMessage({ id: "STATUS_TIMESTAMP_TYPE_3" });
	} else if (requireActualTime === StatusActualTimeType.Ateo) {
		return intl.formatMessage({ id: "STATUS_TIMESTAMP_TYPE_4" });
	} else if (requireActualTime === StatusActualTimeType.Nomination) {
		return intl.formatMessage({ id: "STATUS_TIMESTAMP_TYPE_5" });
	} else if (requireActualTime === StatusActualTimeType.Override) {
		return intl.formatMessage({ id: "TIMESTAMP" });
	} else if (requireActualTime === StatusActualTimeType.Atn) {
		return intl.formatMessage({ id: "STATUS_TIMESTAMP_TYPE_4" });
	} else if (requireActualTime === StatusActualTimeType.Dtn) {
		return intl.formatMessage({ id: "STATUS_TIMESTAMP_TYPE_5" });
	} else if (requireActualTime === StatusActualTimeType.RequiredForSkipper) {
		return intl.formatMessage({ id: "TIMESTAMP" });
	} else {
		return "";
	}
};

export const isActualTimeRequired = (requireActualTime: StatusActualTimeType, session: Session) => {
	if (requireActualTime === StatusActualTimeType.NotRequired) {
		return false;
	} else if (requireActualTime === StatusActualTimeType.RequiredForSkipper) {
		return isSkipper(session) || isShipManager(session);
	} else {
		return true;
	}
};


export const getDefaultStatusForm = (
	statusType: StatusType,
	statusRuleTypes: ChildPropsFromArray<GetStatusRuleTypesQuery, "ruleTypes">[],
	status?: StatusFragment,
): StatusFormInput => ({
	id: status?.id ?? null,
	type: status?.type ?? statusType,
	cargoActionType: status?.cargoActionType || null,
	description: status?.description ?? "",
	color: status?.color ?? "#FFFFFF",
	requireActualTime: status?.requireActualTime ?? StatusActualTimeType.NotRequired,
	estimateType: status?.estimateType ?? null,
	estimateRequirement: status?.estimateRequirement ?? StatusEstimateRequirement.Optional,
	allowComment: status?.allowComment ?? false,
	allowSkipperComment: status?.allowSkipperComment ?? false,
	isInProgress: status?.isInProgress ?? false,
	isFinishedStatus: status?.finishedStatus ?? false,
	isClosedStatus: status?.closedStatus ?? false,
	isCancelledStatus: status?.cancelledStatus ?? false,
	isExpiringStatus: status?.expiringStatus ?? false,
	applyByCustomer: status?.applyByCustomer ?? false,
	applyBySkipper: status?.applyBySkipper ?? false,
	applyByTerminal: status?.applyByTerminal ?? false,
	isLoadedStatus: status?.loadedStatus ?? false,
	isDischargedStatus: status?.dischargedStatus ?? false,
	voyageVisibleBySkipper: status?.voyageVisibleBySkipper ?? false,
	voyageVisibleByCreditor: status?.voyageVisibleByCreditor ?? false,
	voyageEditableBySkipper: status?.voyageEditableBySkipper ?? false,
	voyageEditableByCreditor: status?.voyageEditableByCreditor ?? false,
	callVisibleByLocationOwner: status?.callVisibleByLocationOwner ?? false,
	isRejectStatus: status?.isRejectStatus ?? false,
	isAcceptStatus: status?.isAcceptStatus ?? false,
	notifyShipOwners: status?.notifyShipOwners ?? false,
	notifyShipManagers: status?.notifyShipManagers ?? false,
	notifySkippers: status?.notifySkippers ?? false,
	notifyOperators: status?.notifyOperators ?? false,
	notifyCustomers: status?.notifyCustomers ?? false,
	notifyTerminals: status?.notifyTerminals ?? false,
	includeInNominationExport: status?.includeInNominationExport ?? false,
	isCustomerNominatingStatus: status?.isCustomerNominatingStatus ?? false,
	isSkipperNominatingStatus: status?.isSkipperNominatingStatus ?? false,
	supplierRequired: status?.supplierRequired ?? false,
	skipperRequiredFillInSailingReport: status?.skipperRequiredFillInSailingReport ?? false,
	skipperRequiredFillInWeightVolume: status?.skipperRequiredFillInWeightVolume ?? false,
	shouldUpdateJDE: status?.shouldUpdateJDE ?? false,
	shouldLoadOnTerminal: status?.shouldLoadOnTerminal ?? false,
	laytimeStart: status?.laytimeStartStatus ?? false,
	laytimeEnd: status?.laytimeEndStatus ?? false,
	requireDocuments: status?.requireDocuments ?? false,
	requireTanks: status?.requireTanks ?? false,
	successiveStatuses: status?.successiveStatuses?.map(s => ({
		id: s.id ?? null,
		type: s.type ?? null,
		description: s.description ?? "",
		applyByCustomer: s.applyByCustomer ?? false,
		applyByTerminal: s.applyByTerminal ?? false,
		applyBySkipper: s.applyBySkipper ?? false,
	})) ?? [],
	showConfirmMessage: status?.showConfirmMessage ?? false,
	translations: status?.translations ? {
		id: status.translations.id,
		defaultDescription: status.translations.defaultValue ?? "",
		descriptions: Object.fromEntries(status.translations.descriptions.map(({ key, value }) => [ key, value ])),
	} : {
		id: null,
		defaultDescription: "",
		descriptions: {},
	},
	confirmWindowTranslations: status?.confirmMessages ? {
		id: status.confirmMessages.id,
		defaultDescription: status.confirmMessages.defaultValue ?? "",
		descriptions: Object.fromEntries(status.confirmMessages.descriptions.map(({ key, value }) => [ key, value ])),
	} : {
		id: null,
		defaultDescription: "",
		descriptions: {},
	},
	sendDocument: {
		enabled: status?.sendDocument?.enabled ?? false,
		type: status?.sendDocument?.type ?? null,
		target: status?.sendDocument?.target ?? null,
	},
	rules: [ ...statusRuleTypes ]
		.filter(type => type.sourceType === statusType)
		.map(type => {
			const existingRule = status?.rules?.find(rule => rule.ruleType.name === type.name);

			return {
				id: existingRule?.id ?? null,
				ruleType: type.name,
				targetStatus: existingRule?.targetStatus ?? null,
				ifFitsStatusFlow: existingRule?.ifFitsStatusFlow ?? false,
				ifNotAppliedBefore: existingRule?.ifNotAppliedBefore ?? false,
			};
		}),
});

export const convertStatusToMutationInput = (
	status: StatusFragmentFragment,
	statusRuleTypes?: ChildPropsFromArray<GetStatusRuleTypesQuery, "ruleTypes">[] | undefined,
) => {
	const statusForm = getDefaultStatusForm(status.type, statusRuleTypes ?? [], status);
	return convertStatusFormToMutationInput(statusForm);
};

export const convertStatusFormToMutationInput = (status: StatusFormOutput) => {

	const getValidDescriptions = (descriptions: Record<string, string | null | undefined> | null) => {
		if (descriptions == null) {
			return undefined;
		}

		const validDescriptions = Object.entries(descriptions)
			.filter(([ _, value ]) => value != null && value.trim().length > 0)
			.map(([ key, value ]) => ({ key, value }));

		if (validDescriptions.length === 0) {
			return undefined;
		}

		return validDescriptions;
	};

	return {
		...status,

		rules: status.rules?.filter(r => r.targetStatus != null).map(r => ({
			...r,
			targetStatus: {
				id: r.targetStatus?.id,
			},
		})) ?? [],

		successiveStatuses: status.successiveStatuses?.map(s => ({
			id: s.id,
			type: s.type,
		})) ?? [],

		translations: {
			id: status.translations?.id ?? null,
			defaultDescription: status.translations?.defaultDescription ?? "",
			descriptions: getValidDescriptions(status.translations?.descriptions ?? null),
		},

		confirmWindowTranslations: {
			id: status.confirmWindowTranslations?.id ?? null,
			defaultDescription: status.confirmWindowTranslations?.defaultDescription ?? "",
			descriptions: getValidDescriptions(status.confirmWindowTranslations?.descriptions ?? null),
		},
	};
};

export const dynamicTextColor = (bgColor: string | null | undefined) => {
	if (!bgColor) {
		return undefined;
	}

	const black = { r: 0, g: 0, b: 0 };
	const white = { r: 255, g: 255, b: 255 };

	const bgRGB = colorStringToRGB(bgColor);

	const contrastWithBlack = Math.abs(apcaContrast(black, bgRGB));
	const contrastWithWhite = Math.abs(apcaContrast(white, bgRGB));

	return contrastWithBlack > contrastWithWhite ? "#1a1a1a" : "#f5f5f5";
};

function colorStringToRGB(colorString: string) {
	colorString = colorString.replace("#", "");
	if (colorString.length === 3) {
		colorString = colorString.split("").map(char => char + char).join("");
	}

	return {
		r: parseInt(colorString.slice(0, 2), 16),
		g: parseInt(colorString.slice(2, 4), 16),
		b: parseInt(colorString.slice(4, 6), 16),
	};
}

/**
 * Follows the {@link https://www.w3.org/WAI/GL/task-forces/silver/wiki/User:Myndex/APCA_model APCA Model for Predicting Contract}
 *
 * <img class="marble" src="https://raw.githubusercontent.com/Myndex/apca-w3/master/images/APCAw3_0.1.17_APCA0.0.98G.svg" alt="APCA math">
 * @return Lc (Lightness contrast)
 */
function apcaContrast(txtRGB: { r: number, g: number, b: number }, bgRGB: { r: number, g: number, b: number }) {
	// Convert colors to luminance (soft clamping black levels)
	const Ytxt = fClamp(sRGBtoY(txtRGB));
	const Ybg  = fClamp(sRGBtoY(bgRGB));

	let S; // Perceptual lightness difference
	if (Ybg > Ytxt) {
		// Normal polarity (dark text on a light background)
		S = Ybg ** 0.56 - Ytxt ** 0.57;
	} else {
		// Reverse polarity (light text on a dark background)
		S = Ybg ** 0.65 - Ytxt ** 0.62;
	}

	const contrast = Math.abs(Ybg - Ytxt) < 0.0005 ? 0 : S * 1.14;

	let Sapc; // Clamp minimum contrast, then offset
	if (Math.abs(contrast) < 0.1) {
		Sapc = 0;
	} else if (contrast > 0) {
		Sapc = contrast - 0.027;
	} else {
		Sapc = contrast + 0.027;
	}

	return Sapc * 100; // Lightness contrast
}

/**
 * @param Yc luminance (color) value
 */
function fClamp(Yc: number) {
	if (Yc >= 0.022) {
		return Yc;
	}
	return Yc + (0.022 - Yc) ** 1.414;
}

/**
 * - Convert from 8 bit to decimal 0.0-1.0
 * - Linearize (remove gamma) by applying a 2.4 exponent
 * - Apply sRGB coefficients and sum to get Y (luminance)
 * */
function sRGBtoY(rgb: { r: number, g: number, b: number }) {
	const R = Math.pow(rgb.r / 255, 2.4);
	const G = Math.pow(rgb.g / 255, 2.4);
	const B = Math.pow(rgb.b / 255, 2.4);

	return (0.2126729 * R + 0.7151522 * G + 0.0721750 * B);
}

export type OnlyStatusFlags<T extends object = StatusFragment,> = {
	[K in keyof T]: T[K] extends boolean ? K : never;
}[keyof T];

export function getStatusFieldVisibility(statusType: StatusType, session: Session) {
	const isCargo = statusType === StatusType.Cargo;
	const isCall = statusType === StatusType.Call;
	const isVoyage = statusType === StatusType.Voyage;
	const isDeclaration = statusType === StatusType.Declaration;
	const isInvoice = statusType === StatusType.Invoice;

	return {
		requireActualTime: !isDeclaration && !isInvoice,
		estimateType: isCargo || isCall,
		sendDocument: isCargo,
		allowComment: true,
		allowSkipperComment: !isDeclaration && !isInvoice,
		isInProgress: isVoyage,
		finishedStatus: true,
		closedStatus: isVoyage || isDeclaration || isInvoice,
		cancelledStatus: true,
		expiringStatus: isCargo || isVoyage,
		applyByCustomer: !isDeclaration && !isInvoice,
		applyBySkipper: !isDeclaration && !isInvoice,
		applyByTerminal: !isDeclaration && !isInvoice,
		loadedStatus: isCargo,
		dischargedStatus: isCargo,
		voyageVisibleBySkipper: isVoyage && hasPluginPermission(session, "rightsBasedOnStatus"),
		voyageVisibleByCreditor: isVoyage && hasPluginPermission(session, "rightsBasedOnStatus"),
		voyageEditableBySkipper: isVoyage && hasPluginPermission(session, "rightsBasedOnStatus"),
		voyageEditableByCreditor: isVoyage && hasPluginPermission(session, "rightsBasedOnStatus"),
		callVisibleByLocationOwner: isCall && hasPluginPermission(session, "rightsBasedOnStatus"),
		isRejectStatus: isVoyage,
		isAcceptStatus: isVoyage,
		notifyShipOwners: !isDeclaration && !isInvoice,
		notifySkippers: !isDeclaration && !isInvoice,
		notifyShipManagers: !isDeclaration && !isInvoice,
		notifyOperators: !isDeclaration && !isInvoice,
		notifyCustomers: !isDeclaration && !isInvoice,
		notifyTerminals: !isDeclaration && !isInvoice,
		isCustomerNominatingStatus: !isDeclaration && !isInvoice && hasPluginPermission(session, "nominationCustomer"),
		isSkipperNominatingStatus: isVoyage && hasPluginPermission(session, "nominationShip"),
		supplierRequired: isVoyage,
		skipperRequiredFillInSailingReport: !isDeclaration && !isInvoice,
		skipperRequiredFillInWeightVolume: isVoyage || isCall || isCargo,
		shouldUpdateJDE: !isDeclaration && !isInvoice,
		shouldLoadOnTerminal: !isDeclaration && !isInvoice,
		includeInNominationExport: !isDeclaration && !isInvoice,
		laytimeStartStatus: isCargo || isCall,
		laytimeEndStatus: isCargo || isCall,
		requireDocuments: isCargo || isCall || isVoyage,
		requireTanks: isCargo,
	};
}
